import type ExcelJSType from 'exceljs';
import type { ExcelRow } from './types';

const HEADER_ROW_NUMBER = 1;

async function loadWorksheet(file: File): Promise<ExcelJSType.Worksheet> {
  const ExcelJS = (await import('exceljs')).default;
  const workbook = new ExcelJS.Workbook();
  const buffer = await file.arrayBuffer();
  const isCsv = file.name.toLowerCase().endsWith('.csv');

  if (isCsv) {
    const stream = new Blob([buffer]).stream() as unknown as NodeJS.ReadableStream;
    return workbook.csv.read(stream);
  }

  await workbook.xlsx.load(buffer);
  const [worksheet] = workbook.worksheets;

  if (!worksheet) {
    throw new Error('Spreadsheet is empty.');
  }

  return worksheet;
}

function extractHeaders(worksheet: ExcelJSType.Worksheet): string[] {
  const headerRow = worksheet.getRow(HEADER_ROW_NUMBER).values as unknown[];

  return headerRow
    .map((value) => (typeof value === 'string' ? value.trim() : ''));
}

function rowToObject(row: ExcelJSType.Row, headers: string[]): ExcelRow {
  const excelRow: ExcelRow = {};

  headers.forEach((header, columnIndex) => {
    if (!header) {
      return;
    }

    excelRow[header] = row.getCell(columnIndex).value;
  });

  return excelRow;
}

function isEmptyRow(excelRow: ExcelRow): boolean {
  return Object
    .values(excelRow)
    .every((value) => {
      if (value === null || value === undefined) {
        return true;
      }

      return typeof value === 'string' && value.trim() === '';
    });
}

function extractRows(
  worksheet: ExcelJSType.Worksheet,
  headers: string[],
): ExcelRow[] {
  const rows: ExcelRow[] = [];

  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber === HEADER_ROW_NUMBER) {
      return;
    }

    const excelRow = rowToObject(row, headers);

    if (isEmptyRow(excelRow)) {
      return;
    }

    rows.push(excelRow);
  });

  return rows;
}

export default async function parseExcel(file: File): Promise<ExcelRow[]> {
  const worksheet = await loadWorksheet(file);
  const headers = extractHeaders(worksheet);

  return extractRows(worksheet, headers);
}
